import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export interface StatCardProps {
  title: string;
  value: string;
  /** Secondary line, e.g. remaining currencies for a multi-currency total. */
  hint?: string;
  /** Only pass when a real delta exists. Never fabricate one. */
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: LucideIcon;
  iconColor?: 'primary' | 'secondary' | 'accent';
  isLoading?: boolean;
  className?: string;
}

const ICON_COLORS = {
  primary: 'bg-primary/10 text-primary',
  secondary: 'bg-secondary/10 text-secondary',
  accent: 'bg-accent/10 text-accent',
} as const;

const CHANGE_COLORS = {
  positive: 'text-[#059669] bg-[#059669]/10',
  negative: 'text-[#DC2626] bg-[#DC2626]/10',
  neutral: 'text-muted-foreground bg-muted',
} as const;

export function StatCard({
  title,
  value,
  hint,
  change,
  changeType = 'neutral',
  icon: Icon,
  iconColor = 'accent',
  isLoading = false,
  className,
}: StatCardProps) {
  return (
    <div className={cn('card-elevated p-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {isLoading ? (
            <Skeleton className="h-9 w-28" />
          ) : (
            <p className="font-display text-3xl font-bold tracking-tight">{value}</p>
          )}
          {change && !isLoading && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                CHANGE_COLORS[changeType],
              )}
            >
              {change}
            </span>
          )}
          {hint && !isLoading && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {Icon && (
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
              ICON_COLORS[iconColor],
            )}
          >
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  );
}
